import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, Plus, Search, Filter, Download, Lock, Settings as SettingsIcon, AlertTriangle, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { endpoints } from "@/lib/api";
import EmployeeModal from "@/components/employees/EmployeeModal";
import { toast } from "sonner";

const TRAINING_TONE = {
  Valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Expiring: "bg-amber-50 text-amber-700 border-amber-200",
  Expired: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function EmployeesPage() {
  const [params, setParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [authorized, setAuthorized] = useState(null);
  const [q, setQ] = useState("");
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [department, setDepartment] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });
  const [reminderDays, setReminderDays] = useState(30);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newDays, setNewDays] = useState(30);

  const load = () => {
    const p = {};
    if (clientSlug !== "all") p.client_slug = clientSlug;
    return endpoints.listEmployees(p).then(setEmployees).catch(() => {});
  };
  useEffect(() => {
    endpoints.me().then((u) => endpoints.listConsultants().then((cs) => {
      const me = cs.find((c) => c.name === u.name);
      setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
    }));
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.getTrainingReminderDays().then((r) => { setReminderDays(r.training_reminder_days); setNewDays(r.training_reminder_days); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (authorized) load();
    const n = new URLSearchParams(params);
    if (clientSlug === "all") n.delete("client"); else n.set("client", clientSlug);
    setParams(n, { replace: true });
    // eslint-disable-next-line
  }, [authorized, clientSlug]);

  const departments = useMemo(() => {
    return Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort();
  }, [employees]);

  const filtered = useMemo(() => employees.filter((e) => {
    if (department !== "all" && e.department !== department) return false;
    if (q && !`${e.name} ${e.position} ${e.department} ${e.email} ${e.client_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [employees, q, department]);

  const stats = useMemo(() => {
    const s = { total: employees.length, expiring: 0, expired: 0, gaps: 0, coverage_sum: 0 };
    employees.forEach((e) => {
      s.expiring += e.training_expiring || 0;
      s.expired += e.training_expired || 0;
      s.gaps += (e.competency_gaps || []).length;
      s.coverage_sum += (e.competency_coverage || 0);
    });
    s.coverage = employees.length ? Math.round(s.coverage_sum / employees.length) : 0;
    return s;
  }, [employees]);

  const saveReminderDays = async () => {
    try {
      await endpoints.setTrainingReminderDays(Number(newDays));
      setReminderDays(Number(newDays));
      setSettingsOpen(false);
      toast.success(`Reminder period set to ${newDays} days`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
  };

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto"><div className="glass-card rounded-3xl p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
      <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
      <p className="text-sm text-slate-500">Employee & training records are restricted to ISOQuest staff.</p>
    </div></div>
  );

  const csvHref = endpoints.exportEmployeesCsvUrl(clientSlug !== "all" ? { client_slug: clientSlug } : {});

  const StatCard = ({ label, value, tone, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-md shadow-purple-500/25"><Users className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Employees & Competency</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {employees.length} employees · reminder period {reminderDays} days</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(true)} data-testid="emp-settings-button"><SettingsIcon className="h-3.5 w-3.5 mr-1" /> Reminder settings</Button>
          <Button asChild variant="outline" size="sm" data-testid="emp-export-csv-button"><a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a></Button>
          <Button data-testid="emp-new-button" className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, id: null })}><Plus className="h-4 w-4 mr-1" /> New employee</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Employees" value={stats.total} tone="text-indigo-600" testid="emp-stat-total" />
        <StatCard label="Avg competency coverage" value={`${stats.coverage}%`} tone="text-emerald-600" testid="emp-stat-coverage" />
        <StatCard label="Expiring trainings" value={stats.expiring} tone="text-amber-600" testid="emp-stat-expiring" />
        <StatCard label="Expired trainings" value={stats.expired} tone="text-rose-600" testid="emp-stat-expired" />
      </div>

      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="emp-search-input" placeholder="Search name, position, email..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={clientSlug} onValueChange={setClientSlug}>
          <SelectTrigger data-testid="emp-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger data-testid="emp-dept-filter" className="h-9 w-[180px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No employees match your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((e) => {
            const alert = e.training_expired > 0 || e.training_expiring > 0;
            return (
              <button
                key={e.id}
                data-testid={`emp-card-${e.id}`}
                onClick={() => setModal({ open: true, id: e.id })}
                className={`glass-card rounded-3xl p-5 text-left card-hover ${e.training_expired > 0 ? "ring-2 ring-rose-400/40" : ""}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{e.client_name} · {e.department || "—"}</div>
                    <h3 className="font-semibold text-sm mt-0.5 truncate">{e.name}</h3>
                    <div className="text-[11px] text-slate-500 truncate">{e.position || "—"}</div>
                  </div>
                  {alert && (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className={`h-4 w-4 ${e.training_expired > 0 ? "text-rose-500" : "text-amber-500"}`} />
                    </div>
                  )}
                </div>
                <div className="space-y-1 text-[11px] text-slate-500 mb-3">
                  {e.email && <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{e.email}</span></div>}
                  {e.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{e.phone}</div>}
                </div>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    <span>Competency coverage</span>
                    <span>{e.competency_coverage}%</span>
                  </div>
                  <Progress value={e.competency_coverage} className="h-1.5" />
                </div>
                <div className="flex items-center flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px] rounded-full">{(e.trainings || []).length} trainings</Badge>
                  {e.training_expiring > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expiring}`}>{e.training_expiring} expiring</Badge>}
                  {e.training_expired > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expired}`}>{e.training_expired} expired</Badge>}
                  {(e.competency_gaps || []).length > 0 && <Badge variant="outline" className="text-[10px] rounded-full bg-amber-50 text-amber-700 border-amber-200">{e.competency_gaps.length} gap{e.competency_gaps.length !== 1 ? "s" : ""}</Badge>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <EmployeeModal
        open={modal.open}
        employeeId={modal.id}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        clients={clients}
        onOpenChange={(o) => { if (!o) setModal({ open: false, id: null }); }}
        onChanged={() => { load(); setModal({ open: false, id: null }); }}
      />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="modal-emp-settings">
          <DialogHeader>
            <DialogTitle>Training reminder settings</DialogTitle>
            <DialogDescription>ISOQuest administrators can configure how many days before a certificate expires we start flagging it.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Days before expiry to flag</Label>
            <Input data-testid="emp-reminder-days-input" type="number" min="1" max="365" value={newDays} onChange={(e) => setNewDays(e.target.value)} />
            <p className="text-[11px] text-slate-500 mt-1">Applies to every client's employee training records.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button data-testid="emp-reminder-save" className="brand-gradient text-white" onClick={saveReminderDays}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
